"use client";

import React from "react";
import CardArt, { CardArtProps } from "@/components/Card/CardArt";
import { formatPrice } from "@/lib/moneyHelper";
import { CARD_ASPECT_RATIO, LANDSCAPE_CARD_ASPECT_RATIO } from "@/lib/cardArt";

/**
 * A card with the chrome that goes around it.
 *
 * Every surface that shows a card wants some subset of the same things: a price,
 * a card number, a count of how many are in the deck, a caption underneath, a
 * hover outline, a link or a click. They used to each build that themselves
 * around their own copy of the art.
 *
 * This is the wrapper: art from CardArt, the badges as props, and `children` for
 * anything a surface adds that nobody else needs -- the deck builder's add and
 * subtract buttons, the sealed simulator's "2K Counter" labels. A surface with
 * extra requirements wraps this rather than reimplementing the card.
 */

export interface CardTileProps extends CardArtProps {
  /** Bottom-right badge, e.g. "OP01-001". */
  number?: string;
  /** Bottom-left badge. Null renders nothing, which is the no-price case. */
  price?: number | null;
  /** Top-right badge: copies in the deck. */
  count?: number | string;
  /** Text under the card, e.g. its name or "3x". */
  caption?: React.ReactNode;
  /** Wraps the tile in a link. */
  href?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseMove?: () => void;
  onMouseLeave?: () => void;
  /** Dims the art, for a card already committed elsewhere. */
  dimmed?: boolean;
  /** Overlays drawn over the art: buttons, labels, covers. */
  children?: React.ReactNode;
  /** Classes for the outer wrapper, not the art. */
  wrapperClassName?: string;
  /**
   * Written to the wrapper as `data-id`. The deck builder's keyboard shortcuts
   * find the card under the cursor with `closest("[data-id]")`, so a tile in a
   * deck zone has to carry its id.
   */
  dataId?: string;
}

const BADGE =
  "absolute z-20 m-1 rounded bg-black/50 p-1 text-[11px] font-light text-slate-300 sm:text-xs";

export default function CardTile({
  number,
  price = null,
  count,
  caption,
  href,
  onClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  dimmed,
  children,
  wrapperClassName,
  dataId,
  ...art
}: CardTileProps) {
  const isLandscape = art.orientation === "landscape";

  const tile = (
    <div
      data-id={dataId}
      className={`group relative ${wrapperClassName ?? ""}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <CardArt {...art} />

      {/*
        Chrome hangs off the art, not off the slot. A sideways card does not
        fill its slot -- it sits across the middle of it -- so badges pinned to
        the slot's corners would float in the empty space above and below it.
        This box is the art's own footprint: same width, the art's aspect ratio,
        centred exactly as the art is.
      */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          className="pointer-events-none relative w-full"
          style={{
            aspectRatio: isLandscape
              ? LANDSCAPE_CARD_ASPECT_RATIO
              : CARD_ASPECT_RATIO,
          }}
        >
          {price !== null && price !== undefined && (
            <div className={`${BADGE} bottom-0 left-0`}>
              {formatPrice(price)}
            </div>
          )}
          {number && (
            <div className={`${BADGE} bottom-0 right-0`}>{number}</div>
          )}

          {count !== undefined && count !== 0 && (
            <div className="absolute -right-[10px] -top-[10px] z-20 flex h-5 w-5 items-center justify-center rounded-full bg-white text-sm font-bold text-black outline outline-1 outline-offset-[-1px] outline-gray-400 shadow-[0_0_6px_rgba(0,0,0,0.3)]">
              {count}
            </div>
          )}

          {dimmed && (
            <div className="absolute inset-0 z-10 rounded-[4px] bg-black/40" />
          )}

          {/* Anything a caller layers on sits over the art too, and takes its
              clicks back -- the deck builder's buttons are in here. */}
          {children && (
            <div className="pointer-events-auto absolute inset-0">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const withCaption = caption ? (
    <div>
      {tile}
      <div className="mt-[5px] line-clamp-1 text-center text-sm font-weight-semibold">
        {caption}
      </div>
    </div>
  ) : (
    tile
  );

  if (href) {
    return (
      <a href={href} className="w-full max-w-full">
        {withCaption}
      </a>
    );
  }

  return withCaption;
}
